/**
 * ManageChore - where a poster picks who does the job
 * ---------------------------------------------------
 * Lists everyone who applied with their rating, bio and distance, and gives
 * the poster the four actions they need: accept, make backup, reject, and
 * mark the whole thing complete.
 */

import { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  X,
  Shield,
  CheckCircle2,
  Trash2,
  Pencil,
  AlertTriangle,
  Users,
  MessageSquare,
  Home,
} from 'lucide-react';

import { choreApi, applicationApi } from '../services/api';
import { money, dateTime, countdown, travel } from '../utils/format';
import { choreWhenShort } from '../utils/schedule';
import Badge, { StatusBadge, ApplicationBadge } from '../components/Badge';
import StarRating from '../components/StarRating';
import Avatar from '../components/Avatar';
import Loader from '../components/Loader';
import EmptyState from '../components/EmptyState';
import Modal from '../components/Modal';
import BackLink from '../components/BackLink';
import ReviewForm from '../components/ReviewForm';
import DonationPrompt, { donationPromptIsDue } from '../components/DonationPrompt';

import { useI18n } from '../hooks/useI18n';

export default function ManageChore() {
  const { t } = useI18n();
  const { id } = useParams();
  const navigate = useNavigate();

  const [chore, setChore] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busyId, setBusyId] = useState(null);

  const [confirm, setConfirm] = useState(null); // { type: 'cancel' | 'delete' | 'complete' }
  const [cancelReason, setCancelReason] = useState('');
  const [askForDonation, setAskForDonation] = useState(false);

  /**
   * The review is asked for HERE, the moment the job is marked done, rather
   * than waiting on a dashboard panel (the owner's request, 18 September
   * 2026). It is also put in the bell by the server, so nothing is lost by
   * choosing "not now" - see backend/utils/reviewReminders.js.
   */
  const [reviewNow, setReviewNow] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await choreApi.applications(id);
      setChore(data.chore);
      setApplications(data.applications);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  /** Runs one of the accept/backup/reject actions and refreshes the page. */
  const act = async (applicationId, action, successMessage) => {
    setBusyId(applicationId);
    setError('');
    try {
      await action(applicationId);
      setNotice(successMessage);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleComplete = async () => {
    setConfirm(null);
    try {
      await choreApi.complete(id);
      setNotice(t('manage.completed'));
      await load();
      setReviewNow(true);
      /**
       * BOTH, AND THE DONATION FIRST.
       *
       * Earlier on 18 September 2026 this waited for the review - the idea
       * being one ask at a time, the person before the money. It reads well
       * and it was wrong: somebody who neither writes the review nor presses
       * "not now" simply walks away, and the ask never happens at all. The
       * owner noticed within a day - "the donation window no longer shows up
       * when the user marks a chore as completed".
       *
       * So it is back where it was. The donation is a modal over the page and
       * the review form is on the page underneath it, so closing the one
       * leaves the other sitting there - which is the order they wanted
       * without either of them being conditional on the other.
       *
       * Still only once the chore has ACTUALLY been marked done, and still
       * only if the ask is not snoozed (a month after a dismissal, a year
       * after somebody gives - see components/DonationPrompt.jsx). If
       * anything above threw, the person has a problem to deal with and
       * should not meet an appeal.
       */
      if (donationPromptIsDue()) setAskForDonation(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCancel = async () => {
    setConfirm(null);
    try {
      await choreApi.cancel(id, cancelReason);
      setNotice(t('manage.cancelled'));
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async () => {
    setConfirm(null);
    try {
      await choreApi.remove(id);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <Loader label={t('manage.loading')} />;

  if (!chore) {
    return (
      <div className="container section">
        <div className="alert alert-error">{error || t('manage.notFound')}</div>
      </div>
    );
  }

  const open = chore.status === 'open';
  const assigned = chore.status === 'assigned';
  const finished = ['completed', 'cancelled'].includes(chore.status);

  // Applications still in play appear first; closed ones are shown after.
  const active = applications.filter((a) => ['pending', 'accepted', 'backup'].includes(a.status));
  const closed = applications.filter((a) => ['rejected', 'withdrawn'].includes(a.status));

  // The student doing the job, for the "Message" button. Read from the
  // applications because this page's chore carries only their id.
  const booked = applications.find(
    (a) => a.status === 'accepted' && a.student
      && String(a.student._id) === String(chore.assignedStudent)
  )?.student;
  const canMessage = Boolean(booked) && ['assigned', 'completed'].includes(chore.status);

  // A chore from before the address was required - see ChoreDetail.jsx.
  const addressMissing = !chore.location?.address && !finished;

  return (
    <div className="container section">
      <BackLink to="/dashboard" label={t('manage.back')} />

      {notice && <div className="alert alert-success" style={{ marginBottom: 'var(--space-sm)' }}>{notice}</div>}

      {/* ---------------- THE REVIEW, WHILE IT IS FRESH ---------------- */}
      {reviewNow && booked && !chore.reviewedByPoster && (
        <div
          className="card"
          style={{ marginBottom: 'var(--space-md)', borderColor: 'var(--color-amber)' }}
        >
          <h3 style={{ marginBottom: 'var(--space-sm)' }}>{t('manage.reviewHeading')}</h3>

          <ReviewForm
            choreId={chore._id}
            subjectName={booked.firstName}
            onDone={() => {
              setReviewNow(false);
              setNotice(t('manage.reviewSaved'));
              load();
            }}
          />

          <button
            type="button"
            className="btn btn-text btn-sm"
            style={{ marginTop: 'var(--space-xs)' }}
            onClick={() => setReviewNow(false)}
          >
            {t('manage.reviewNotNow')}
          </button>
        </div>
      )}
      {error && <div className="alert alert-error" style={{ marginBottom: 'var(--space-sm)' }}>{error}</div>}

      {/* ---------------- CHORE SUMMARY ---------------- */}
      <div className="card" style={{ marginBottom: 'var(--space-lg)' }}>
        <div className="card-header">
          <div>
            <div className="row" style={{ gap: 'var(--space-xs)', marginBottom: 6 }}>
              <StatusBadge status={chore.status} />
              <Badge tone="soft-blue">{t(`categories.${chore.category}`)}</Badge>
              {assigned && !chore.backupStudent && (
                <Badge tone="amber" icon={AlertTriangle}>
                  {t('manage.noBackupChosen')}
                </Badge>
              )}
            </div>
            <h1 style={{ fontSize: 'var(--text-h3)' }}>{chore.title}</h1>
            <div className="card-meta" style={{ marginTop: 6 }}>
              {/* This is where a poster lands straight after posting, so it is
                  the first place a 26-visit booking has to look like one. The
                  date on its own is the NEXT visit, and showing only that made
                  three months of babysitting read as a single Wednesday. */}
              <span>
                {chore.isMultiDay
                  ? `${chore.visitCount} visits · next ${choreWhenShort(chore)}`
                  : choreWhenShort(chore)}
              </span>
              <span>{countdown(chore.hoursUntilStart)}</span>
              <span>{chore.location?.city}</span>
            </div>
          </div>
          <span className="pay-amount-large">
            {money(chore.isMultiDay ? chore.totalPay : chore.pay)}
            {chore.isMultiDay && (
              <span className="pay-each">{money(chore.pay)} × {chore.visitCount}</span>
            )}
          </span>
        </div>

        <div className="card-footer">
          <Link to={`/chores/${chore._id}`} className="btn btn-ghost btn-sm btn-inline">
            {t('manage.viewPublicPage')}
          </Link>

          <div className="row" style={{ gap: 'var(--space-xs)' }}>
            {canMessage && (
              <Link to={`/messages/${chore._id}`} className="btn btn-outline btn-sm btn-inline">
                <MessageSquare size={16} aria-hidden="true" />
                {t('choreDetail.messageName', { name: booked.firstName })}
              </Link>
            )}

            {!finished && (
              <Link to={`/chores/${chore._id}/edit`} className="btn btn-outline-blue btn-sm btn-inline">
                <Pencil size={16} /> {t('common.edit')}
              </Link>
            )}

            {assigned && (
              <button
                type="button"
                className="btn btn-primary btn-sm btn-inline"
                onClick={() => setConfirm({ type: 'complete' })}
              >
                <CheckCircle2 size={16} /> {t('manage.markComplete')}
              </button>
            )}

            {open && applications.length === 0 && (
              <button
                type="button"
                className="btn btn-ghost btn-sm btn-inline"
                onClick={() => setConfirm({ type: 'delete' })}
              >
                <Trash2 size={16} /> {t('manage.deleteChore')}
              </button>
            )}

            {!finished && (
              <button
                type="button"
                className="btn btn-ghost btn-sm btn-inline"
                onClick={() => setConfirm({ type: 'cancel' })}
              >
                {t('manage.cancelChore')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ---------------- NO ADDRESS ---------------- */}
      {addressMissing && (
        <div className="alert alert-warning" style={{ marginBottom: 'var(--space-md)' }}>
          <Home size={20} aria-hidden="true" />
          <div>
            {t('choreDetail.noAddressOwner')}{' '}
            <Link to={`/chores/${chore._id}/edit`}>{t('choreDetail.addAddress')}</Link>
          </div>
        </div>
      )}

      {/* ---------------- BACKUP NUDGE ---------------- */}
      {assigned && !chore.backupStudent && active.some((a) => a.status === 'pending') && (
        <div className="alert alert-warning" style={{ marginBottom: 'var(--space-md)' }}>
          <Shield size={20} aria-hidden="true" />
          <div>
            <strong>{t('manage.considerBackup')}</strong> {t('manage.considerBackupBody')}
          </div>
        </div>
      )}

      {/* ---------------- APPLICATIONS ---------------- */}
      <h2 style={{ fontSize: 'var(--text-h3)', marginBottom: 'var(--space-sm)' }}>
        Applications ({applications.length})
      </h2>

      {applications.length === 0 ? (
        <EmptyState
          icon={Users}
          title={t('manage.noApplications')}
          message={t('manage.noApplicationsBody')}
        />
      ) : (
        <div className="stack" style={{ gap: 'var(--space-sm)' }}>
          {[...active, ...closed].map((app) => {
            const student = app.student;
            if (!student) return null;

            const isClosed = ['rejected', 'withdrawn'].includes(app.status);
            const busy = busyId === app._id;

            return (
              <div className="card" key={app._id} style={isClosed ? { opacity: 0.65 } : undefined}>
                <div className="profile-card">
                  <Avatar person={student} />

                  <div className="profile-card-body">
                    <div className="row-between">
                      <div className="row" style={{ gap: 'var(--space-xs)' }}>
                        <Link to={`/students/${student._id}`} className="profile-name">
                          {student.firstName} {student.lastName}
                        </Link>
                        <Badge tone="soft-blue">{student.schoolYear}</Badge>
                        {student.age && <span className="text-sm text-muted">{t('manage.ageYears', { age: student.age })}</span>}
                      </div>
                      <ApplicationBadge status={app.status} />
                    </div>

                    <div className="row" style={{ gap: 'var(--space-sm)', marginTop: 4 }}>
                      <StarRating
                        value={student.rating?.average || 0}
                        count={student.rating?.count || 0}
                        size={14}
                      />
                      <span className="text-sm text-muted">
                        {student.stats?.choresCompleted || 0} chore
                        {student.stats?.choresCompleted === 1 ? '' : 's'} completed
                      </span>
                      {app.distanceKm !== null && app.distanceKm !== undefined && (
                        <span className="text-sm text-muted">{travel(app)}</span>
                      )}
                    </div>

                    {student.bio && (
                      <p className="text-sm text-muted" style={{ marginTop: 'var(--space-xs)' }}>
                        {student.bio}
                      </p>
                    )}

                    {app.message && (
                      <div
                        className="card"
                        style={{
                          background: 'var(--color-bg-subtle)',
                          boxShadow: 'none',
                          padding: 'var(--space-xs) var(--space-sm)',
                          marginTop: 'var(--space-xs)',
                        }}
                      >
                        <p className="text-sm">&ldquo;{app.message}&rdquo;</p>
                      </div>
                    )}

                    {/* ---- Buttons, depending on where things stand ---- */}
                    {!finished && !isClosed && (
                      <div className="row" style={{ gap: 'var(--space-xs)', marginTop: 'var(--space-sm)' }}>
                        {open && app.status === 'pending' && (
                          <button
                            type="button"
                            className="btn btn-primary btn-sm btn-inline"
                            disabled={busy}
                            onClick={() =>
                              act(
                                app._id,
                                applicationApi.accept,
                                `${student.firstName} has been accepted and told.`
                              )
                            }
                          >
                            <Check size={16} /> {t('manage.accept')}
                          </button>
                        )}

                        {assigned && app.status === 'pending' && (
                          <button
                            type="button"
                            className="btn btn-outline-blue btn-sm btn-inline"
                            disabled={busy}
                            onClick={() =>
                              act(
                                app._id,
                                applicationApi.makeBackup,
                                `${student.firstName} is now the backup.`
                              )
                            }
                          >
                            <Shield size={16} /> {t('manage.makeBackup')}
                          </button>
                        )}

                        {app.status !== 'accepted' && (
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm btn-inline"
                            disabled={busy}
                            onClick={() =>
                              act(app._id, applicationApi.reject, `${student.firstName} was turned down.`)
                            }
                          >
                            <X size={16} />
                            {app.status === 'backup' ? t('manage.removeBackup') : t('manage.turnDown')}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ---------------- CONFIRMATIONS ---------------- */}
      <Modal
        open={confirm?.type === 'complete'}
        onClose={() => setConfirm(null)}
        title={t('manage.completeQ')}
        actions={
          <>
            <button type="button" className="btn btn-ghost btn-inline" onClick={() => setConfirm(null)}>
              {t('manage.notYet')}
            </button>
            <button type="button" className="btn btn-primary btn-inline" onClick={handleComplete}>
              {t('manage.yesItIsDone')}
            </button>
          </>
        }
      >
        <p>
          Only do this once the work has actually been done and you have paid the{' '}
          {money(chore.pay)}. It lets both of you leave a review, and counts towards the
          student&apos;s record.
        </p>
      </Modal>

      <DonationPrompt open={askForDonation} onClose={() => setAskForDonation(false)} />

      <Modal
        open={confirm?.type === 'cancel'}
        onClose={() => setConfirm(null)}
        title={t('manage.cancelQ')}
        actions={
          <>
            <button type="button" className="btn btn-ghost btn-inline" onClick={() => setConfirm(null)}>
              {t('manage.keepIt')}
            </button>
            <button type="button" className="btn btn-danger btn-inline" onClick={handleCancel}>
              {t('manage.cancelTheChore')}
            </button>
          </>
        }
      >
        <p style={{ marginBottom: 'var(--space-sm)' }}>
          {t('manage.cancelBody')}
        </p>
        <div className="field">
          <label htmlFor="cancel-reason">{t('manage.cancelReason')}</label>
          <textarea
            id="cancel-reason"
            value={cancelReason}
            onChange={(event) => setCancelReason(event.target.value)}
            maxLength={300}
            rows={3}
            placeholder={t('manage.cancelPlaceholder')}
          />
        </div>
      </Modal>

      <Modal
        open={confirm?.type === 'delete'}
        onClose={() => setConfirm(null)}
        title={t('manage.deleteQ')}
        actions={
          <>
            <button type="button" className="btn btn-ghost btn-inline" onClick={() => setConfirm(null)}>
              {t('manage.keepIt')}
            </button>
            <button type="button" className="btn btn-danger btn-inline" onClick={handleDelete}>
              {t('manage.deletePermanently')}
            </button>
          </>
        }
      >
        <p>{t('manage.deleteBody')}</p>
      </Modal>
    </div>
  );
}
